import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolSite,
  ProtocolWarning,
} from "../types";

/**
 * Tinzaparin — Innohep, the third low-molecular-weight heparin, in a syringe
 * with an orange safety device she bends back over the needle when she is
 * done.
 *
 * ### Two labels, one procedure
 *
 * The UK leaflet (emc, LEO, where the product is sold as "tinzaparin sodium
 * syringe") and the French Innohep notice (ANSM, LEO) carry the same
 * self-injection section, numbered eleven steps in English and nine in
 * French: hands washed and dried; the liquid inspected, not if cloudy or
 * with sediment, yellow is still fine; sitting, standing or lying where she
 * can see the skin; the syringe out of its plastic tube by bending the lid
 * back, at room temperature; the orange safety device bent away from the
 * cap, the cap off without bending the needle, the needle touching nothing;
 * **the air bubble kept** unless the prescribed dose is less than the
 * syringe, in which case the excess is pressed out needle-up; the syringe
 * like a pen, a fold between thumb and forefinger, **at a right angle,
 * pointing straight and not at an angle**, the needle fully in; the plunger
 * slowly over ten to fifteen seconds with the fold held and no drawing back;
 * the needle promptly out, the fold let go, **no rubbing or massaging**; the
 * safety device bent back along the needle and pushed down on a table until
 * it clicks, then on to 45 degrees; the syringe to the sharps container at
 * once, never the household rubbish. The English copy is the UK leaflet's
 * and the French the French notice's.
 *
 * ### Where they differ
 *
 * The UK leaflet's "if you forget, talk to your doctor or nurse as soon as
 * you remember" has no French counterpart (the notice says "sans objet"),
 * so the French missed-dose line is the author's reading of the UK one,
 * flagged in the fiche. The French notice adds the alcohol swab and the
 * tissue for blood; the UK leaflet adds the 5 cm from the navel and the 3 cm
 * from the last site. Lower back and upper arm are on both and offered on
 * neither: the site question knows the abdomen and the thigh.
 *
 * ### The unit
 *
 * International units of anti-Xa activity, by weight for treatment and a
 * fixed syringe for prophylaxis; no strength list, since the prescription
 * may be any point on a graduated syringe.
 *
 * Restated from `content/data/medications/anticoagulants.json` (the
 * `Innohep` entry), written from these two labels.
 */

const TINZAPARIN_BRANDS = [
  { name: "Innohep", markets: ["GB", "EU", "FR"] },
] as const satisfies readonly ProtocolBrand[];

/** The UK leaflet's line; the French notice has none, so the French is the author's. */
const MISSED_DOSE: ProtocolWarning = {
  id: "tinzaparin.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - If you forget to have your injection, it is important that you talk to your doctor or nurse as soon as you remember and get advice on what to do.",
    fr: "Dose oubliée - Si vous oubliez votre injection, il est important d'en parler à votre médecin ou à votre infirmière dès que vous vous en rendez compte, pour savoir quoi faire.",
  },
  sourceSegment:
    "⚠ Missed dose - If you forget to have your injection, it is important that you talk to your doctor or nurse as soon as you remember and get advice on what to do.",
};

/** The UK leaflet's "you must get urgent medical help", routed to emergency. */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "tinzaparin.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing, swelling of your face or throat, a severe rash, or blistering or peeling of the skin, mouth, eyes or genitals can be a serious allergic reaction: stop and get urgent medical help straight away.",
    fr: "Aide urgente - Des difficultés à respirer, un gonflement du visage ou de la gorge, une éruption cutanée sévère, ou des cloques ou une desquamation de la peau, de la bouche, des yeux ou des parties génitales peuvent être une réaction allergique grave : arrêtez et appelez les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing, swelling of your face or throat, a severe rash, or blistering or peeling of the skin, mouth, eyes or genitals can be a serious allergic reaction: stop and get urgent medical help straight away.",
};

/** Severe bleeding to the clinic now; bruising at the site is the common one. */
const BLEEDING: ProtocolWarning = {
  id: "tinzaparin.bleeding",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Bleeding - Tell your doctor straight away if you spot any sign that you may be starting to bleed severely, or if you bleed or bruise more easily than usual. Bruising, pain or a small lump where the injection was given is common and is not a reason to stop.",
    fr: "Saignement - Prévenez immédiatement votre médecin si vous remarquez un signe de saignement important, ou si vous saignez ou avez des bleus plus facilement que d'habitude. Un hématome, une douleur ou une petite boule au point d'injection sont fréquents et ne sont pas une raison d'arrêter.",
  },
  sourceSegment:
    "⚠ Bleeding - Tell your doctor straight away if you spot any sign that you may be starting to bleed severely, or if you bleed or bruise more easily than usual. Bruising, pain or a small lump where the injection was given is common and is not a reason to stop.",
};

/**
 * "The right or left side of the abdomen, at least 5 cm from your belly
 * button; the sides of your thigh, lower back, upper leg or upper arm." The
 * site question knows the first two.
 */
const SITES: readonly ProtocolSite[] = [
  {
    id: "abdomen",
    label: { en: "Abdomen", fr: "Ventre" },
    copy: {
      en: "Choose and clean the site - Inject here, on the right or left side of your abdomen, at least 5 cm from your belly button, and alternate between the left and the right side. Avoid scar tissue, stretch marks, areas with visible blood vessels and areas that are tender, bruised, red or hard; change the place you inject each time and stay at least 3 cm from your last injection site. Clean the skin with an alcohol swab and let it dry without rubbing.",
      fr: "Choisir et désinfecter le site - Injectez ici, sur le ventre, dans la ceinture abdominale, à au moins 5 cm du nombril, en alternant entre le côté gauche et le côté droit. Évitez les cicatrices, les vergetures, les zones où des vaisseaux sanguins sont visibles et les zones sensibles, meurtries, rouges ou dures ; changez de site d'injection à chaque fois et restez à au moins 3 cm du site de votre dernière injection. Nettoyez la peau en passant une compresse d'alcool et laissez sécher sans frotter.",
    },
  },
  {
    id: "thigh",
    label: { en: "Side of thigh", fr: "Côté de la cuisse" },
    copy: {
      en: "Choose and clean the site - Inject here, on the side of your thigh. Avoid scar tissue, stretch marks, areas with visible blood vessels and areas that are tender, bruised, red or hard; change the place you inject each time and stay at least 3 cm from your last injection site. Clean the skin with an alcohol swab and let it dry without rubbing.",
      fr: "Choisir et désinfecter le site - Injectez ici, sur la face antéro-externe de la cuisse. Évitez les cicatrices, les vergetures, les zones où des vaisseaux sanguins sont visibles et les zones sensibles, meurtries, rouges ou dures ; changez de site d'injection à chaque fois et restez à au moins 3 cm du site de votre dernière injection. Nettoyez la peau en passant une compresse d'alcool et laissez sécher sans frotter.",
    },
  },
];

export const TINZAPARIN_PROTOCOL: InjectionProtocol = {
  medicationSlug: "tinzaparin",
  brands: TINZAPARIN_BRANDS,
  aliases: ["Tinzaparin", "Tinzaparine"],
  indication: "anticoagulant",
  dose: { unit: "IU" },
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  route: "subcutaneous",
  version: "2026-10-06.1",
  derivedFrom: "content/data/medications/anticoagulants.json#tinzaparin",
  sites: SITES,
  steps: [
    {
      id: "tinzaparin.syringe.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Give the injection only once you have been shown how, exactly as you have been shown. Thoroughly wash and dry your hands. Sit, stand or lie in a position where you can see the skin you are going to inject. Lay out your prefilled syringe of {brand}, an alcohol swab, a tissue and a sharps container; the syringe should be at room temperature, which can minimise discomfort. Look at the liquid: do not use it if you notice cloudiness or sediment, but it may turn yellow during storage and is still usable. {brand} is given under the skin, ideally at the same time every day; do not inject it into a muscle.",
        fr: "Préparer - Ne faites l'injection qu'après qu'on vous a montré comment faire, et exactement comme on vous l'a montré. Lavez-vous soigneusement les mains et séchez-les. Installez-vous assise, debout ou allongée de façon à voir la peau que vous allez piquer. Disposez votre seringue préremplie de {brand}, une compresse d'alcool, un mouchoir en papier et un collecteur ; la seringue doit être à température ambiante, ce qui peut réduire la gêne. Inspectez le contenu : ne l'utilisez pas s'il présente un aspect trouble ou des particules, mais il peut devenir jaune pendant le stockage et reste utilisable. {brand} s'injecte sous la peau, de préférence à la même heure chaque jour ; ne l'injectez pas par voie intramusculaire.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "tinzaparin.syringe.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: SITES[0]!.copy,
    },
    {
      // The bubble stays unless the dose is less than the syringe: the one
      // syringe in the catalogue she may have to press down before the skin.
      id: "tinzaparin.syringe.ready-the-syringe",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Ready the syringe - Carefully take the syringe out of its plastic container by bending the lid all the way back and sliding the syringe out. Bend the orange safety device down, away from the cap on the needle. Remove the protective needle cap without bending the needle, and make sure the needle does not touch anything. There is no need to remove the air bubble if you need the total quantity in the syringe for your dose; do not pull on the plunger. If the dose you have been told to have{dose} is less than the amount in the syringe, hold the syringe vertically with the needle pointing upwards and gently remove the excess by pressing the plunger into the syringe.",
        fr: "Préparer la seringue - Ouvrez le tube en soulevant complètement le couvercle coloré et sortez la seringue. Éloignez le système de sécurité orange du capuchon protecteur de l'aiguille. Ôtez le capuchon protecteur de l'aiguille sans tordre l'aiguille, et veillez à ce que l'aiguille ne touche rien. Il n'est pas nécessaire d'éjecter la bulle d'air si vous avez besoin de toute la quantité contenue dans la seringue pour votre dose ; ne tirez pas sur le piston. Si la dose prescrite{dose} est inférieure à la quantité contenue dans la seringue, tenez la seringue verticalement, aiguille vers le haut, et évacuez doucement le volume excédentaire en poussant sur le piston.",
      },
    },
    {
      // "At a right angle to your body (pointing straight, not at an angle)"
      // is the label's own phrase for 90°; the figure keeps its mark.
      id: "tinzaparin.syringe.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Hold the syringe in your writing hand like you would hold a pen. With your other hand, make a fold of skin by gently pinching the area with your thumb and forefinger. With the syringe at a right angle to your body, pointing straight and not at an angle, insert the needle fully into the skin fold. Continue to hold the skin fold and, without drawing back on the syringe, press down on the plunger slowly over 10 to 15 seconds. Then promptly pull the needle completely out of the skin and let go of the skin fold. Do not rub or massage the place where you injected, as this can lead to bruising.",
        fr: "Injecter - Tenez la seringue dans la main avec laquelle vous écrivez, comme un stylo. De l'autre main, pincez la peau entre le pouce et l'index de façon à réaliser un pli cutané. Avec la seringue à angle droit par rapport à votre corps, bien droite et non en biais, introduisez complètement l'aiguille dans le pli cutané. Continuez à tenir le pli et, sans tirer sur le piston, appuyez lentement sur le piston pendant 10 à 15 secondes. Retirez ensuite rapidement et complètement l'aiguille de la peau, puis lâchez le pli. Ne frottez pas et ne massez pas l'endroit où vous avez injecté, car cela peut provoquer un hématome.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      // The orange safety device: bent back along the needle and pushed down
      // on a table after the needle is out, as the Fragmin Needle-Trap.
      id: "tinzaparin.syringe.remove-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Lock the needle - Using a hard surface, bend the orange safety device back so it points in the same direction as the needle. Then, with the safety device flat against a hard surface such as a table, gently push downwards until the needle clicks into the device, and continue to push downwards so that the needle and device are at a 45 degree angle to the syringe. Wipe any trace of blood with a tissue.",
        fr: "Verrouiller l'aiguille - En vous appuyant sur une surface dure, repositionnez le système de sécurité orange dans sa position initiale, le long de l'aiguille. Puis, le système de sécurité à plat sur une surface dure comme une table, appuyez doucement vers le bas jusqu'à ce que l'aiguille soit verrouillée dans le dispositif avec un clic, et continuez à appuyer jusqu'à ce que l'aiguille et le dispositif forment un angle de 45 degrés avec la seringue. Essuyez toute trace de sang avec un mouchoir en papier.",
      },
    },
    {
      id: "tinzaparin.syringe.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Handle the used syringe with care even with the safety device in place, and put it in your sharps container immediately; if one is not at hand, put it back in its plastic container and press the lid down until it clicks, then dispose of it as instructed. Never put syringes or needles in the household rubbish. Choose a different site for your next injection. {brand} needs no special storage conditions; use a syringe immediately once opened, and not after its expiry date.",
        fr: "Après l'injection - Manipulez la seringue utilisée avec précaution même avec le système de sécurité en place, et mettez-la immédiatement dans votre collecteur ; si vous n'en avez pas sous la main, replacez-la dans son tube avec l'aiguille dirigée vers le bas et refermez le couvercle, puis éliminez-la selon les instructions. Ne jetez jamais de seringues ou d'aiguilles avec les ordures ménagères. Choisissez un site d'injection différent pour l'injection suivante. {brand} ne nécessite pas de précautions particulières de conservation ; après ouverture, la seringue doit être utilisée immédiatement, et jamais après la date de péremption.",
      },
      warnings: [BLEEDING],
    },
  ],
};
